import {useEffect, useState} from "react";
import CookieOutlined from "@mui/icons-material/CookieOutlined";
import {useMessages} from "i18n";
import {useConsent} from "consent/useConsent";
import {showsWithdrawalControl, WITHDRAWAL_CONTROL_PROPERTIES, withdrawalControlProperties} from "consent/withdrawalControl";
import {useIsomorphicLayoutEffect} from "utils/useIsomorphicLayoutEffect";
import {CookieSettingsDialog} from "legal/CookieSettingsDialog";
import {LauncherButton} from "./CookieSettingsLauncher.styles";

/**
 * The always-present way back into the consent choices (kitecorp/transferify-ui#2675), mounted
 * app-wide in {@link RootLayout} beside the banner.
 *
 * One press from any page re-opens the Cookie Settings dialog, where each purpose can be granted or
 * withdrawn. Whether it shows at all is `consent/withdrawalControl`'s rule, with the reasoning; this
 * component renders a button and owns nothing else.
 *
 * It opens the SAME dialog the banner's Customize action and the Resources menu open, rather than a
 * second panel of its own: a third answer to "where are the cookie choices" is how the three drift
 * apart, and the legal bodies now name this control by the dialog's own title.
 *
 * The accessible name is `nav.resourcesMenu.cookieSettings` — the catalogue string the Resources row
 * already uses — so the two entry points cannot end up called different things in the three
 * locales, and the cookie policy's sentence about "the Cookie Settings button" stays true. `title`
 * carries it as well, because the glyph alone is not a label for a sighted pointer user.
 *
 * SSG-safe through the same `mounted` guard the banner uses, and for the same reason: `useConsent`
 * seeds its state from `getConsent()`, which in the BROWSER reads localStorage on the very first
 * render. A returning visitor who already decided would therefore hydrate a button the pre-rendered
 * HTML does not contain, which React rejects as a hydration mismatch and which takes the whole page
 * down with it (measured as React error #418 across 36 landing specs). Deferring to an effect keeps
 * this out of the static markup AND out of the first client render.
 *
 * While it is on the page it publishes its own box on the document root (`withdrawalControlProperties`,
 * kitecorp/transferify#536), as the banner publishes its reserve, because content a page fixes to the window's
 * bottom has to know where the corner is taken: the recipient page's campaign button ran under it on a phone.
 */
export function CookieSettingsLauncher() {
	const {consent} = useConsent();
	const {nav} = useMessages();
	const [open, setOpen] = useState(false);
	const [mounted, setMounted] = useState(false);
	const [button, setButton] = useState<HTMLButtonElement | null>(null);

	useEffect(() => {
		setMounted(true);
	}, []);

	// Before paint, so content already on the page that reads the box moves clear of it in the same frame the
	// control first paints in.
	useIsomorphicLayoutEffect(() => {
		if (button === null) return undefined;
		const root = document.documentElement;
		const publish = () => {
			const {top, right, bottom} = button.getBoundingClientRect();
			for (const [name, value] of withdrawalControlProperties({top, right, bottom})) {
				if (root.style.getPropertyValue(name) !== value) root.style.setProperty(name, value);
			}
		};
		publish();
		// Fixed, so only the window moves it: its inset tightens below the phone breakpoint.
		window.addEventListener("resize", publish);
		return () => {
			window.removeEventListener("resize", publish);
			for (const name of WITHDRAWAL_CONTROL_PROPERTIES) root.style.removeProperty(name);
		};
	}, [button]);

	if (!mounted || !showsWithdrawalControl(consent)) {
		return null;
	}

	return (
		<>
			<LauncherButton
				ref={setButton}
				type="button"
				aria-label={nav.resourcesMenu.cookieSettings}
				title={nav.resourcesMenu.cookieSettings}
				onClick={() => setOpen(true)}
			>
				<CookieOutlined fontSize="small"/>
			</LauncherButton>
			<CookieSettingsDialog open={open} onClose={() => setOpen(false)}/>
		</>
	);
}
